/**
 * Governed Agentic Rewrite Mission — the score → agent-fixes → rescore loop,
 * run through the GOVERNED write path.
 *
 * The auditor gap this closes: generated drafts used to bypass governance —
 * a rewrite clobbered the section with no diff, no provenance, no gate.
 * This module shapes every section rewrite as a diffed, reversible,
 * provenance-sealed approval proposal:
 *
 *   buildRewritePrompt ──▶ worker.rewrite (REAL argoWorker seam)
 *          │                     │  null / throw / timeout
 *          │                     ▼
 *          │            deterministicRewrite (pure, never regresses score)
 *          ▼                     │
 *   Brand Brain facts ───────────┤
 *                                ▼
 *          checkFidelity (banned-term gate, fidelity.ts semantics)
 *                                ▼
 *          verifyContract (outcomeContract/outcomeVerifier spirit:
 *            min score + answer-first block + internal links)
 *                                ▼
 *          toApprovalAction — reversible, ledger-ready, NEVER auto-applied
 *
 * PURE except `runRewrite`'s single injected side effect (worker.rewrite).
 * No store, no firebase, no fabricated customer data — every fact in the
 * prompt and the fallback flows from the caller-supplied request.
 */

import {
  scoreContent,
  type KeywordTarget,
  type ScoreInput,
} from './seoScore';
import { buildRewriteDiff, type DiffResult } from './scoreRewrite';
import { djb2Hash } from '../brandBrain/compose';
import { DEFAULT_FIDELITY_PENALTY, FIDELITY_PENALTY } from '../brandBrain/fidelity';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The old→new trust-mechanic diff a rewrite proposal carries — the same
 *  reviewable line-diff shape `scoreRewrite.buildRewriteDiff` produces
 *  (agent/actionDiff spirit: see exactly what changes BEFORE apply). */
export type ActionDiff = DiffResult;

/** The outcome contract a rewrite must satisfy before it can be applied. */
export interface OutcomeSpec {
  /** Minimum post-rewrite section score (0..100). */
  minScore: number;
  /** Must the section open with a ≤55-word answer-first block? */
  requireAnswerBlock: boolean;
  /** Minimum count of internal (`/…`) markdown links. */
  minInternalLinks: number;
}

/** One section-rewrite request. `brandFacts` mirrors the scorer's shape so
 *  prompt injection and the fidelity gate grade against the SAME facts. */
export interface RewriteRequest {
  draftId: string;
  sectionHeading: string;
  sectionMarkdown: string;
  targets: KeywordTarget[];
  brandFacts: ScoreInput['brandFacts'];
  contract?: OutcomeSpec;
}

/** The injectable worker seam. Production adapts `agent/argoWorker`;
 *  tests inject a fake. `null` means "no worker output — fall back". */
export interface WorkerPort {
  rewrite(prompt: string): Promise<{ text: string } | null>;
}

/** Sealed provenance for one rewrite: which brand facts + which prompt +
 *  which source produced the proposal. Ledger-ready plain data. */
export interface ProvenanceEnvelope {
  draftId: string;
  sectionHeading: string;
  brandFactIds: string[];
  promptHash: string;
  source: 'worker' | 'fallback';
  model?: string;
  ts: number;
}

/** The full gated result of one rewrite mission. */
export interface RewriteResult {
  proposedMarkdown: string;
  source: 'worker' | 'fallback';
  scoreBefore: number;
  scoreAfter: number;
  fidelity: { score: number; bannedHits: string[] };
  contractMet: boolean;
  contractReport: string[];
  provenance: ProvenanceEnvelope;
  diff: ActionDiff;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** The answer-first block the mission demands and the fallback writes. */
export const ANSWER_WORD_LIMIT = 55;

/** How long `runRewrite` waits for the worker before falling back. */
export const WORKER_TIMEOUT_MS = 25_000;

// ---------------------------------------------------------------------------
// Text helpers (deterministic, local — no DOM, no clock)
// ---------------------------------------------------------------------------

/** Markdown → plain prose, mirroring the scorer's strip. */
function stripMd(md: string): string {
  return (md ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\|/g, ' ')
    .replace(/[*_>]/g, '');
}

function words(text: string): string[] {
  return text.split(/\s+/).filter((w) => w.length > 0);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Case-insensitive whole-phrase occurrence count (scorer semantics). */
function countTerm(haystack: string, term: string): number {
  const t = term.trim();
  if (t.length === 0) return 0;
  const re = new RegExp(`\\b${escapeRegExp(t)}\\b`, 'gi');
  return (haystack.match(re) ?? []).length;
}

/** Deterministic URL slug. */
function slugify(text: string): string {
  const slug = (text ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 48)
    .replace(/-$/, '');
  return slug.length > 0 ? slug : 'untitled';
}

/** Score a section deterministically with a fixed framing (heading as the
 *  title, empty meta) so before/after deltas are attributable to the body. */
function scoreSection(req: RewriteRequest, markdown: string): number {
  const input: ScoreInput = {
    title: req.sectionHeading,
    meta: '',
    markdown,
    targets: req.targets,
  };
  if (req.brandFacts) input.brandFacts = req.brandFacts;
  return scoreContent(input).total;
}

/** Split a section into its leading heading line (if any) and body. */
function sectionBody(sectionMarkdown: string): string {
  const lines = (sectionMarkdown ?? '').split('\n');
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i++;
  if (i < lines.length && /^#{1,6}\s+\S/.test(lines[i])) i++;
  return lines.slice(i).join('\n').trim();
}

const QUESTION_OPENER =
  /^(what|how|why|when|where|which|who|whose|can|could|should|would|do|does|did|is|are|will)\b/i;

/** Ensure a heading reads as a question (AEO: question-shaped H2). */
export function questionHeading(heading: string): string {
  const h = (heading ?? '').trim().replace(/[.:]+$/, '');
  if (h.length === 0) return 'What should you know?';
  if (h.endsWith('?')) return h;
  if (QUESTION_OPENER.test(h)) return `${h}?`;
  return `What should you know about ${h}?`;
}

/** Does the section open (after headings) with a ≤55-word paragraph? */
export function hasAnswerBlock(markdown: string): boolean {
  const lines = (markdown ?? '').split('\n');
  const para: string[] = [];
  for (const line of lines) {
    if (/^#{1,6}\s/.test(line)) {
      if (para.length > 0) break;
      continue;
    }
    if (line.trim() === '') {
      if (para.length > 0) break;
      continue;
    }
    para.push(line.trim());
  }
  const n = words(stripMd(para.join(' '))).length;
  return n > 0 && n <= ANSWER_WORD_LIMIT;
}

/** Count internal markdown links — `[label](/path)` targets only. */
export function countInternalLinks(markdown: string): number {
  return ((markdown ?? '').match(/\]\(\/[^)]+\)/g) ?? []).length;
}

// ---------------------------------------------------------------------------
// buildRewritePrompt — Brand Brain injected, answer-first instructed. PURE.
// ---------------------------------------------------------------------------

/** Deterministic ids for the brand facts that shaped a rewrite — sealed
 *  into provenance so "which brand knowledge produced this?" is answerable. */
function brandFactIdsOf(brandFacts: RewriteRequest['brandFacts']): string[] {
  if (!brandFacts) return [];
  return [
    ...brandFacts.voiceTerms.map((t) => `voice:${t}`),
    ...brandFacts.bannedTerms.map((t) => `banned:${t}`),
    ...brandFacts.proofPoints.map((p) => `proof:${djb2Hash(p)}`),
  ];
}

/**
 * Compose the worker prompt for one section rewrite. PURE and deterministic:
 * same request → byte-identical prompt (and therefore stable promptHash).
 * Injects brand voice / proof points / banned terms (brandBrain/compose
 * semantics: rules first, evidence after), the keyword targets, and the
 * AEO answer-first instruction. Facts come ONLY from the request.
 */
export function buildRewritePrompt(req: RewriteRequest): string {
  const bf = req.brandFacts;
  const lines: string[] = [
    'You are rewriting ONE section of a governed content draft. Return only the rewritten section markdown.',
    '',
    'BRAND BRAIN:',
    `Voice (write in this register): ${bf && bf.voiceTerms.length > 0 ? bf.voiceTerms.join(', ') : '(none provided)'}`,
    `Banned terms (never use any of these): ${bf && bf.bannedTerms.length > 0 ? bf.bannedTerms.join(', ') : '(none provided)'}`,
    'Proof points (the ONLY claims you may assert — never invent facts):',
    ...(bf && bf.proofPoints.length > 0 ? bf.proofPoints.map((p) => `- ${p}`) : ['- (none provided)']),
    '',
    'TARGET TERMS (weave each in naturally, within its use range):',
    ...(req.targets.length > 0
      ? req.targets.map((t) => `- ${t.term} (${t.kind}, ${t.minUses}–${t.maxUses} uses)`)
      : ['- (none)']),
    '',
    'INSTRUCTIONS:',
    `1. Answer first: open the section with a direct answer of at most ${ANSWER_WORD_LIMIT} words.`,
    '2. Keep the H2 question-shaped so answer engines can lift it.',
    '3. Include at least one internal link to a related page.',
    '4. Preserve every factual claim already in the section — rewrite the prose, not the truth.',
    '',
    `SECTION HEADING: ${req.sectionHeading}`,
    'SECTION MARKDOWN:',
    req.sectionMarkdown,
  ];
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// deterministicRewrite — the always-works offline path. PURE.
// ---------------------------------------------------------------------------

/**
 * Deterministic section rewrite: prepend a ≤55-word direct-answer block,
 * ensure the H2 is question-shaped, weave under-used target terms in as
 * explicit draft notes (the shipped `applyFix` add-term pattern), and add
 * an internal-link slot. Guaranteed to never regress the section score:
 * if the transformed candidate would score below the original, the
 * original is returned unchanged. Pure — same input, same output.
 */
export function deterministicRewrite(req: RewriteRequest): string {
  const body = sectionBody(req.sectionMarkdown);
  const question = questionHeading(req.sectionHeading);

  // ≤55-word answer-first block, built only from the section's own prose
  // (plus the primary term, which the caller supplied as a target).
  const primary = req.targets.find((t) => t.kind === 'primary')?.term;
  const plainBody = stripMd(body).trim();
  const firstSentence = (plainBody.match(/^[^.!?\n]*[.!?]?/) ?? [''])[0].trim();
  const core =
    firstSentence.length > 0
      ? firstSentence
      : `${req.sectionHeading.trim()} — the essentials are covered below.`;
  const opening = primary ? `In short — ${primary}: ${core}` : `In short: ${core}`;
  const answerWords = words(opening).slice(0, ANSWER_WORD_LIMIT);
  let answer = answerWords.join(' ');
  if (!/[.!?]$/.test(answer)) answer = `${answer}.`;

  const parts: string[] = [`## ${question}`, '', answer];
  if (body.length > 0) parts.push('', body);

  // Weave under-used target terms as explicit, reviewable draft notes.
  const soFar = stripMd(parts.join('\n'));
  const mentions: string[] = [];
  for (const t of req.targets) {
    const need = t.minUses - countTerm(soFar, t.term);
    for (let i = 0; i < need; i++) mentions.push(`work "${t.term}" into this section`);
  }
  if (mentions.length > 0) parts.push('', `[Draft note: ${mentions.join('; ')}]`);

  // Internal-link slot.
  parts.push('', `See also: [${question}](/blog/${slugify(req.sectionHeading)})`);

  const candidate = parts.join('\n');
  // Non-regression guarantee: never propose something that scores worse.
  return scoreSection(req, candidate) >= scoreSection(req, req.sectionMarkdown)
    ? candidate
    : req.sectionMarkdown;
}

// ---------------------------------------------------------------------------
// checkFidelity — Brand Brain gate (fidelity.ts semantics). PURE.
// ---------------------------------------------------------------------------

const round4 = (x: number) => Math.round(x * 10000) / 10000;

/**
 * Grade output against the request's brand facts using the Brand Brain
 * fidelity semantics: each banned-term hit is a concrete violation that
 * reduces the 0..1 score by the lexicon penalty (`brandBrain/fidelity`'s
 * published grading policy). Banned-term presence FAILS the gate. No
 * facts → honest 1.0 with no hits (no rules ⇒ no evidence of violation).
 */
export function checkFidelity(
  markdown: string,
  brandFacts: RewriteRequest['brandFacts'],
): { score: number; bannedHits: string[] } {
  const banned = brandFacts?.bannedTerms ?? [];
  const haystack = (markdown ?? '').toLowerCase();
  const bannedHits = banned.filter(
    (t) => t.trim().length > 0 && haystack.includes(t.toLowerCase()),
  );
  const perHit = FIDELITY_PENALTY.lexicon ?? DEFAULT_FIDELITY_PENALTY;
  const score = round4(Math.min(1, Math.max(0, 1 - bannedHits.length * perHit)));
  return { score, bannedHits };
}

// ---------------------------------------------------------------------------
// verifyContract — outcome-contract enforcement. PURE.
// ---------------------------------------------------------------------------

/**
 * Machine-check a rewrite against its outcome contract (outcomeVerifier
 * spirit: reality replayed against the before-claim, every clause gets an
 * auditable line). `met` is true only when EVERY clause passes; each failed
 * clause contributes its own ✗ report line.
 */
export function verifyContract(
  spec: OutcomeSpec,
  result: { scoreAfter: number; hasAnswer: boolean; internalLinks: number },
): { met: boolean; report: string[] } {
  const report: string[] = [];
  let met = true;

  if (result.scoreAfter >= spec.minScore) {
    report.push(`✓ score ${result.scoreAfter} ≥ contracted minimum ${spec.minScore}`);
  } else {
    met = false;
    report.push(`✗ score ${result.scoreAfter} is below the contracted minimum ${spec.minScore}`);
  }

  if (spec.requireAnswerBlock) {
    if (result.hasAnswer) {
      report.push(`✓ answer-first block present (≤${ANSWER_WORD_LIMIT} words)`);
    } else {
      met = false;
      report.push('✗ required answer-first block is missing');
    }
  } else {
    report.push('✓ answer block not required by this contract');
  }

  if (result.internalLinks >= spec.minInternalLinks) {
    report.push(`✓ internal links ${result.internalLinks} ≥ required ${spec.minInternalLinks}`);
  } else {
    met = false;
    report.push(
      `✗ internal links ${result.internalLinks} below required ${spec.minInternalLinks}`,
    );
  }

  return { met, report };
}

// ---------------------------------------------------------------------------
// runRewrite — real worker, deterministic fallback, gated result
// ---------------------------------------------------------------------------

/** Race a worker call against the timeout. Rejections propagate (caller
 *  catches); the timer only ever resolves null, and is cleared on settle. */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise<T | null>((resolve, reject) => {
    const timer = setTimeout(() => resolve(null), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * Run one governed rewrite mission. Calls the REAL worker seam first; on
 * null / throw / timeout it degrades to {@link deterministicRewrite} with
 * honest `source: 'fallback'` provenance — this function NEVER rejects.
 * The result is fully gated (fidelity + outcome contract) and diffed, but
 * NOTHING is applied here: the caller shapes it into an approval-queue
 * entry via {@link toApprovalAction}.
 */
export async function runRewrite(
  req: RewriteRequest,
  worker: WorkerPort,
): Promise<RewriteResult> {
  const prompt = buildRewritePrompt(req);
  const scoreBefore = scoreSection(req, req.sectionMarkdown);

  let proposedMarkdown: string | null = null;
  let source: 'worker' | 'fallback' = 'fallback';
  try {
    const out = await withTimeout(worker.rewrite(prompt), WORKER_TIMEOUT_MS);
    if (out !== null && typeof out.text === 'string' && out.text.trim().length > 0) {
      proposedMarkdown = out.text;
      source = 'worker';
    }
  } catch {
    // Worker unreachable or malformed — the deterministic path takes over.
  }
  if (proposedMarkdown === null) {
    proposedMarkdown = deterministicRewrite(req);
    source = 'fallback';
  }

  const scoreAfter = scoreSection(req, proposedMarkdown);
  const fidelity = checkFidelity(proposedMarkdown, req.brandFacts);

  // Default contract: never regress, answer first, links optional.
  const spec: OutcomeSpec = req.contract ?? {
    minScore: scoreBefore,
    requireAnswerBlock: true,
    minInternalLinks: 0,
  };
  const contract = verifyContract(spec, {
    scoreAfter,
    hasAnswer: hasAnswerBlock(proposedMarkdown),
    internalLinks: countInternalLinks(proposedMarkdown),
  });

  // Fidelity gate: banned-term presence fails the mission regardless of score.
  let contractMet = contract.met;
  const contractReport = [...contract.report];
  if (fidelity.bannedHits.length > 0) {
    contractMet = false;
    contractReport.push(
      `✗ brand fidelity gate failed — banned terms present: ${fidelity.bannedHits.join(', ')}`,
    );
  } else {
    contractReport.push(`✓ brand fidelity ${fidelity.score} — no banned terms`);
  }

  const provenance: ProvenanceEnvelope = {
    draftId: req.draftId,
    sectionHeading: req.sectionHeading,
    brandFactIds: brandFactIdsOf(req.brandFacts),
    promptHash: djb2Hash(prompt),
    source,
    ts: Date.now(),
  };

  return {
    proposedMarkdown,
    source,
    scoreBefore,
    scoreAfter,
    fidelity,
    contractMet,
    contractReport,
    provenance,
    diff: buildRewriteDiff(req.sectionMarkdown, proposedMarkdown),
  };
}

// ---------------------------------------------------------------------------
// toApprovalAction — the governed write shape. NEVER applies anything.
// ---------------------------------------------------------------------------

/**
 * Shape a gated rewrite into a reversible, ledger-ready approval-queue
 * entry: the reviewable diff (the before-text sealed inside IS the undo —
 * reversal.ts spirit) plus the sealed provenance envelope. Pure plain data:
 * no store handle, no mutation, no side effect — the proposal enters the
 * approval queue and becomes state ONLY through the existing governed
 * apply path. `reversible` is `true` by construction.
 */
export function toApprovalAction(result: RewriteResult): {
  diff: ActionDiff;
  provenance: ProvenanceEnvelope;
  reversible: true;
} {
  return {
    diff: {
      original: result.diff.original,
      proposed: result.diff.proposed,
      lines: result.diff.lines.map((l) => ({ ...l })),
      summary: result.diff.summary,
    },
    provenance: { ...result.provenance, brandFactIds: [...result.provenance.brandFactIds] },
    reversible: true,
  };
}

// ---------------------------------------------------------------------------
// Section helpers for the thin UI (pure)
// ---------------------------------------------------------------------------

/** Extract one H2 section (heading line through the line before the next
 *  H1/H2) from a full draft. Null when the heading is not found. */
export function extractSection(markdown: string, heading: string): string | null {
  const lines = (markdown ?? '').split('\n');
  const want = heading.trim().toLowerCase();
  let start = -1;
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^##\s+(.*\S)\s*$/);
    if (m && m[1].trim().toLowerCase() === want) {
      start = i;
      break;
    }
  }
  if (start === -1) return null;
  let end = lines.length;
  for (let i = start + 1; i < lines.length; i++) {
    if (/^#{1,2}\s+\S/.test(lines[i])) {
      end = i;
      break;
    }
  }
  return lines.slice(start, end).join('\n').replace(/\s+$/, '');
}

/** Replace one H2 section with new markdown. Unchanged input when the
 *  heading is not found — a stale apply must never clobber other prose. */
export function replaceSection(
  markdown: string,
  heading: string,
  newSection: string,
): string {
  const lines = (markdown ?? '').split('\n');
  const want = heading.trim().toLowerCase();
  let start = -1;
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^##\s+(.*\S)\s*$/);
    if (m && m[1].trim().toLowerCase() === want) {
      start = i;
      break;
    }
  }
  if (start === -1) return markdown;
  let end = lines.length;
  for (let i = start + 1; i < lines.length; i++) {
    if (/^#{1,2}\s+\S/.test(lines[i])) {
      end = i;
      break;
    }
  }
  return [...lines.slice(0, start), ...newSection.split('\n'), ...lines.slice(end)].join('\n');
}
